import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ArrowLeft, BookOpenCheck, Loader2 } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { financeAPI } from '../services/api';

const formatCurrency = (value) => `Rp ${Number(value || 0).toLocaleString('id-ID')}`;
const formatCount = (value) => Number(value || 0).toLocaleString('id-ID');

export default function FinanceSubscriptionDetail() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [moduleData, setModuleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const returnQuery = searchParams.toString();
  const period = useMemo(() => {
    const scope = searchParams.get('scope') === 'year' ? 'year' : 'month';
    const now = new Date();
    const yearValue = Number(searchParams.get('year'));
    const monthValue = Number(searchParams.get('month'));
    return {
      scope,
      year: Number.isInteger(yearValue) && yearValue >= 2000 && yearValue <= 2100 ? yearValue : now.getFullYear(),
      ...(scope === 'month' ? {
        month: Number.isInteger(monthValue) && monthValue >= 1 && monthValue <= 12 ? monthValue : now.getMonth() + 1
      } : {})
    };
  }, [searchParams]);
  const periodLabel = period.scope === 'year'
    ? String(period.year)
    : `${period.year}-${String(period.month).padStart(2, '0')}`;

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    financeAPI.getDashboardSummary(period)
      .then((response) => {
        if (!active) return;
        setModuleData(response.data?.data?.modules?.softwareSubscriptionRevenue || null);
      })
      .catch((requestError) => {
        if (!active) return;
        setError(requestError.response?.data?.message || requestError.message || t('finance.subscriptionDetail.loadError'));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [period, t]);

  const excludedDevices = Number(moduleData?.excludedInactiveDevices || 0)
    + Number(moduleData?.excludedNotInstalledDevices || 0);

  return (
    <div className="admin-text-primary space-y-6 p-4 sm:p-6 lg:p-8">
      <Link to={`/finance${returnQuery ? `?${returnQuery}` : ''}`} className="inline-flex min-h-11 items-center gap-2 rounded-lg px-2 text-sm font-black text-[var(--admin-accent)]">
        <ArrowLeft size={18} />
        {t('finance.subscriptionDetail.back')}
      </Link>
      <header className="max-w-5xl">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-violet-100 text-violet-700"><BookOpenCheck size={24} /></span>
          <div>
            <h2 className="text-2xl font-black text-[var(--admin-text-primary)] sm:text-3xl">{t('finance.sevenModules.softwareSubscriptionRevenue.title')}</h2>
            <p className="mt-1 text-sm text-[var(--admin-text-muted)]">{t('finance.subscriptionDetail.period', { period: periodLabel })}</p>
          </div>
        </div>
      </header>

      {loading ? (
        <div className="admin-card flex min-h-48 max-w-5xl items-center justify-center rounded-xl text-[var(--admin-text-muted)]">
          <Loader2 className="mr-2 animate-spin" size={20} />
          {t('finance.subscriptionDetail.loading')}
        </div>
      ) : error ? (
        <div className="max-w-5xl rounded-xl border border-rose-200 bg-rose-50 p-5 font-bold text-rose-800">{error}</div>
      ) : moduleData?.amount !== null && moduleData?.amount !== undefined ? (
        <section className="admin-card max-w-5xl space-y-5 rounded-xl p-5 sm:p-6">
          <div className="rounded-xl border border-violet-200 bg-violet-50 p-5">
            <p className="text-sm font-black text-violet-800">{t('finance.subscriptionDetail.calculatedAmount')}</p>
            <p className="mt-2 text-3xl font-black text-violet-950">{formatCurrency(moduleData.amount)}</p>
            <p className="mt-2 text-sm leading-6 text-violet-900">{t('finance.subscriptionDetail.calculatedHint')}</p>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              ['deviceMonths', formatCount(moduleData.billableDeviceMonths)],
              ['feePerDevice', formatCurrency(moduleData.feePerDeviceMonth)],
              ['activeSnapshot', formatCount(moduleData.activeDeviceSnapshotCount)],
              ['excludedDevices', formatCount(excludedDevices)]
            ].map(([key, value]) => (
              <article key={key} className="rounded-lg border border-[var(--admin-border-subtle)] bg-[var(--admin-surface-soft)] p-4">
                <p className="text-xs font-black text-[var(--admin-text-muted)]">{t(`finance.subscriptionDetail.metrics.${key}`)}</p>
                <p className="mt-2 break-words text-xl font-black text-[var(--admin-text-primary)]">{value}</p>
              </article>
            ))}
          </div>

          <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-950">
            <AlertTriangle className="mt-0.5 shrink-0" size={22} />
            <div>
              <h3 className="font-black">{t('finance.subscriptionDetail.provisionalTitle')}</h3>
              <p className="mt-2 text-sm leading-6">{t('finance.subscriptionDetail.provisionalDescription', { fallbackCount: formatCount(moduleData.installationFallbackCount) })}</p>
            </div>
          </div>

          <div>
            <h3 className="font-black text-[var(--admin-text-primary)]">{t('finance.subscriptionDetail.monthBreakdown')}</h3>
            <div className="mt-3 overflow-x-auto rounded-lg border border-[var(--admin-border-subtle)]">
              <table className="w-full min-w-[520px] text-left text-sm">
                <thead className="bg-[var(--admin-surface-soft)] text-xs font-black text-[var(--admin-text-muted)]">
                  <tr>
                    <th className="p-3">{t('finance.subscriptionDetail.table.month')}</th>
                    <th className="p-3 text-right">{t('finance.subscriptionDetail.table.devices')}</th>
                    <th className="p-3 text-right">{t('finance.subscriptionDetail.table.amount')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(moduleData.monthBreakdown || []).map((row) => (
                    <tr key={row.monthYear} className="border-t border-[var(--admin-border-subtle)]">
                      <td className="p-3 font-mono font-bold">{row.monthYear}</td>
                      <td className="p-3 text-right font-mono">{formatCount(row.billableDevices)}</td>
                      <td className="p-3 text-right font-mono font-black">{formatCurrency(row.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      ) : (
        <section className="admin-card max-w-5xl rounded-xl p-5 sm:p-6">
          <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-950">
            <AlertTriangle className="mt-0.5 shrink-0" size={22} />
            <div>
              <h3 className="font-black">{t('finance.subscriptionDetail.unavailableTitle')}</h3>
              <p className="mt-2 text-sm leading-6">{t('finance.subscriptionDetail.unavailableDescription')}</p>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
